import React, { useState } from "react";
import { categoryById, isSensitive } from "@/lib/categories";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Copy,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  Shield,
  Calendar,
  Tag,
} from "lucide-react";
import { toast } from "sonner";

export default function SecretDetailDrawer({ open, onOpenChange, secret, onEdit }) {
  const { user } = useAuth();
  const [revealed, setRevealed] = useState({});

  if (!secret) return null;
  const cat = categoryById(secret.category);

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(String(value ?? ""));
      toast.success("Panoya kopyalandı");
      try {
        await api.post(`/secrets/${secret.secret_id}/reveal`);
      } catch {}
    } catch {
      toast.error("Kopyalama başarısız");
    }
  };

  const reveal = async (key) => {
    const next = !revealed[key];
    setRevealed((r) => ({ ...r, [key]: next }));
    if (next) {
      try {
        await api.post(`/secrets/${secret.secret_id}/reveal`);
      } catch {}
      setTimeout(() => {
        setRevealed((r) => ({ ...r, [key]: false }));
      }, 10000);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/secrets/${secret.secret_id}`);
      toast.success("Kayıt silindi");
      window.dispatchEvent(new CustomEvent("vaultops:refresh"));
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silme başarısız");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="secret-detail-drawer"
        className="bg-[#0d121f] border-slate-800 text-slate-100 w-full sm:max-w-lg overflow-y-auto"
      >
        <SheetHeader className="pb-4 border-b border-slate-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="font-display text-xl text-slate-100 break-all">
                {secret.title}
              </SheetTitle>
              <SheetDescription className="text-slate-400 flex items-center gap-2 mt-1">
                <Badge
                  variant="outline"
                  className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-mono text-[10px] uppercase tracking-widest"
                >
                  {cat.label}
                </Badge>
                {secret.admin_only && (
                  <Badge className="bg-rose-500/10 border-rose-500/30 text-rose-400 border font-mono text-[10px] uppercase tracking-widest">
                    <Shield className="w-3 h-3 mr-1" /> Admin
                  </Badge>
                )}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="py-4 space-y-3">
          {cat.fields.map((f) => {
            const value = secret.fields?.[f.key];
            if (!value) return null;
            const sensitive = f.type === "password" || isSensitive(f.key);
            const show = revealed[f.key];
            return (
              <div
                key={f.key}
                className="p-3 rounded-lg bg-[#111726] border border-slate-800"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                    {f.label}
                  </div>
                  <div className="flex items-center gap-1">
                    {sensitive && (
                      <button
                        onClick={() => reveal(f.key)}
                        data-testid={`reveal-${f.key}`}
                        className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-400"
                      >
                        {show ? (
                          <EyeOff className="w-3.5 h-3.5" />
                        ) : (
                          <Eye className="w-3.5 h-3.5" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => copy(f.key, value)}
                      data-testid={`copy-${f.key}`}
                      className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-400"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div
                  className={`font-mono text-sm break-all ${
                    sensitive && !show ? "secret-dots" : "text-slate-100"
                  }`}
                >
                  {sensitive && !show ? "••••••••••••" : String(value)}
                </div>
              </div>
            );
          })}

          {secret.tags?.length > 0 && (
            <div className="p-3 rounded-lg bg-[#111726] border border-slate-800">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
                Etiketler
              </div>
              <div className="flex flex-wrap gap-2">
                {secret.tags.map((t) => (
                  <Badge
                    key={t}
                    variant="secondary"
                    className="bg-slate-800 text-slate-300 border border-slate-700 font-mono text-[10px]"
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {t}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="p-3 rounded-lg bg-[#111726] border border-slate-800 text-xs text-slate-400 flex items-center gap-2 font-mono">
            <Calendar className="w-3.5 h-3.5" />
            Güncellendi: {new Date(secret.updated_at).toLocaleString("tr-TR")}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-4 border-t border-slate-800">
          <Button
            onClick={() => onEdit(secret)}
            data-testid="secret-edit-button"
            className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
          >
            <Pencil className="w-4 h-4 mr-1.5" /> Düzenle
          </Button>
          {(user?.role === "admin" || secret.created_by === user?.user_id) && (
            <Button
              onClick={handleDelete}
              data-testid="secret-delete-button"
              variant="outline"
              className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
